/* eslint-disable @next/next/no-html-link-for-pages */

import { ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { Button, ButtonSizes, FontWeights, minWidth, TextButton } from '@calm-web/design-system';
import { AppIcon } from '@calm-web/icons';

import giftCardMessages from '@/components/gift/Hero/messages';
import { Logo, Navigation, Wrapper } from '@/components/header/Header/styles';
import GridWrapper from '@/components/ui/GridWrapper';
import { useAnalytics } from '@/hooks/analytics';
import { useLoginForm } from '@/hooks/forms';
import { useThemeState, useUserState } from '@/hooks/store';
import { usePinnedHeader } from '@/hooks/ui/usePinnedHeader';
import { setTheme } from '@/store/theme/actions';
import { generatePartnerUrl } from '@/utils/generatePartnerUrl';

import messages from './messages';

const Container = styled.div`
	font-size: 0.9em;
	color: ${palette('white')};

	${minWidth('desktop')} {
		color: ${palette('gray7')};
	}

	a {
		position: relative;

		/* Implement the link underline ourselves to get some nice vertical spacing */
		text-decoration: none;
		&:after {
			content: '';
			height: 1px;
			left: 0px;
			position: absolute;
			right: 0px;
			top: 100%;
			background: ${palette('white')};
			${minWidth('desktop')} {
				background: ${palette('gray7')};
			}
		}

		&:hover {
			top: -2px;

			&:after {
				transform: translateY(2px);
			}
		}
	}
`;

const QuestionsContainer = styled.span`
	display: none;

	${minWidth('mobileLarge')} {
		display: inline;
	}
`;

const QuestionsButton = styled(Button).attrs({
	size: ButtonSizes.Sm,
	backgroundColor: 'white',
	textColor: 'gray7',
})`
	border: 1px solid ${palette('gray7')};
	margin-left: 1em;
	font-size: 0.9em;
`;

const LogOutButton = styled(TextButton)`
	margin-left: 1em;
	font-weight: 100;
	text-decoration: none;
	font-size: 0.9em;
	color: ${palette('white')};

	${minWidth('desktop')} {
		color: ${palette('gray7')};
	}
`;

interface Props {
	isStatic?: boolean;
	isRedeem: boolean;
	isAnnual?: boolean;
	handleCtaClick?: () => void;
}

const GiftCardHeader = ({ isStatic, isRedeem, isAnnual, handleCtaClick }: Props) => {
	const { formatMessage } = useIntl();
	const isPinned = usePinnedHeader();
	const dispatch = useDispatch();

	const user = useUserState();
	const theme = useThemeState();
	const { onLogout } = useLoginForm();
	const { logEvent } = useAnalytics();

	const redeemClick = () => {
		logEvent({ eventName: 'Header : Redeem : Clicked' });
	};

	const onCtaClick = () => {
		logEvent({ eventName: 'Header : Buy Gift : Clicked' });
		if (handleCtaClick) {
			handleCtaClick();
		}
	};

	useEffect(
		() => {
			const headerTheme = isStatic || isPinned ? 'light' : 'dark';

			dispatch(
				setTheme({
					header: headerTheme,
				}),
			);
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isStatic, isPinned],
	);

	const isDark = theme.header === 'dark';
	const logoSrc = theme.header === 'light' ? '/_n/images/calm-logo-gradient.png' : '/_n/images/calm-logo.png';

	return (
		<Wrapper isStatic={isStatic} isDark={isDark}>
			<GridWrapper>
				<Navigation>
					<a href="/">
						{isAnnual ? <AppIcon width={56} height={56} /> : <Logo src={logoSrc} alt="Calm logo" />}
					</a>
					{isRedeem ? (
						<Container>
							<QuestionsContainer>
								{formatMessage(messages.questions)}
								<QuestionsButton href={generatePartnerUrl('contact', user)}>
									{formatMessage(messages.contact)}
								</QuestionsButton>
							</QuestionsContainer>
							{user?.token ? (
								<LogOutButton onClick={onLogout}>{formatMessage(messages.logOut)}</LogOutButton>
							) : null}
						</Container>
					) : isAnnual ? (
						<Container>
							<Button
								fullWidth
								onPress={onCtaClick}
								maxWidth="335px"
								fontWeight={FontWeights.Demi}
								backgroundColor={isDark ? 'buttonSecondaryBg' : 'accessibleBrandGradient'}
								textColor={isDark ? 'buttonSecondaryText' : 'white'}
								borderColor="blackAlpha20"
							>
								{formatMessage(giftCardMessages.cta)}
							</Button>
						</Container>
					) : (
						<Container>
							{formatMessage(messages.navbarRedeem, {
								redeemlink: (chunks: ReactNode) => (
									<a href="/redeem" onClick={redeemClick}>
										{chunks}
									</a>
								),
							})}
						</Container>
					)}
				</Navigation>
			</GridWrapper>
		</Wrapper>
	);
};

export default GiftCardHeader;
